import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { serverNow } from "@/shared/time/clock";
import { MOVE_IN_WHILE_AWAY } from "@/features/pets/adoptFlow";
import { backAtPhrase } from "@/features/pets/backAt";
import { nowLine, presenceGroup } from "@/features/pets/residentView";

/**
 * 入住被拦（409 pet_away）时的等待（2026-09-25 巡检 P1：新号领养了一位正在外面旅行的居民，这是第一只宠物，
 * 守卫把地图、“我的”都带回入住页，页面上只有一句“还在外面”，没有别的可做，也不知道要等多久）。
 * 同日用户拍板第 1 种：领养了在外面的居民也能先入住（能力 identity.move_in_while_away）。开关开着时入住直接成功，
 * 这一页只在服务端仍回 409 pet_away 时兜底；标签页里记着的旧等待不再认，并清掉（巡检 R9-01）。
 * - 回来的时间只用 409 details.back_at，说法只用共用的 backAtPhrase（features/pets/backAt.ts，迎接确认框也用它）：
 *   “{名字} 还在外面，大约 19:23 回到驿站。到时候点一下就能接 TA 回家。”；说不准（null、认不出）时“{名字} 应该快回到驿站了，过一会儿再来接 TA。”；
 *   老服务端根本没有这个字段时不说时间、也不说快回来了（沿用原来那句）；
 * - 到点不替主人发入住（入住是写操作，还会登记到站那条问候）：把“接 TA 回家”变成主按钮、句子换成
 *   “{名字} 应该已经回到驿站了，点一下接 TA 回家。”，由主人点；点了又回 409 就按新的 back_at 再等；页面上不写倒计时；
 * - 此刻在做什么：只用公开居民数据的 presence 和 doing（与居民主页同一份数据、同一种说法）；拿不到、或数据说 TA 不在外面，就不说；
 * - 去处：TA 的居民主页、访客星球（两页都挂在 BareLayout 下，不过入口守卫，入住没完成也进得去；tests/claude-6c2b-movein-away.test.tsx 钉住）；
 * - 这一块放在页面顶上的卡片里（不吸底，巡检 R8-A06），吸底区只留按钮。
 */

/** 刚才的 409 说 TA 在外面：是哪一只、计划几点回到驿站（undefined＝服务端没给这个字段）、是什么时候知道的（本机时钟）。 */
export interface AwayWait {
  petId: string;
  backAt: string | null | undefined;
  seenAt: number;
}

/** 服务端刚说还在外面后，至少过这么久才把“接 TA 回家”变成主按钮（本机时钟偏快时，免得刚点完又立刻叫你点）。 */
export const AWAY_CLOCK_GRACE_MS = 60_000;
/** 等到点时多久对一次钟（不显示，只为到点那一下换按钮和句子）。 */
export const AWAY_TICK_MS = 30_000;

/** 从 409 pet_away 的 details 里取回来的时间：字符串照原样、null 照原样，没有这个字段是 undefined。 */
export function backAtOf(details: Record<string, unknown> | null | undefined): string | null | undefined {
  if (!details || !Object.prototype.hasOwnProperty.call(details, "back_at")) return undefined;
  const value = details.back_at;
  return typeof value === "string" && value.trim() ? value : null;
}

const awayKey = (userId: string) => `petsoul:movein-away:${userId}`;

/** 这个账号刚才在等哪一只（只存本标签页）：去星球逛一圈、被守卫带回入住页时，还是这一份等待，不重新发请求。 */
export function readAway(userId: string | null | undefined): AwayWait | null {
  if (!userId) return null;
  try {
    const raw = sessionStorage.getItem(awayKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AwayWait>;
    if (typeof parsed.petId !== "string" || !parsed.petId || typeof parsed.seenAt !== "number" || !Number.isFinite(parsed.seenAt)) return null;
    const backAt = typeof parsed.backAt === "string" ? parsed.backAt : parsed.backAt === null ? null : undefined;
    return { petId: parsed.petId, backAt, seenAt: parsed.seenAt };
  } catch {
    return null;
  }
}

export function rememberAway(userId: string | null | undefined, wait: AwayWait): void {
  if (!userId) return;
  try {
    sessionStorage.setItem(awayKey(userId), JSON.stringify(wait));
  } catch {
    /* 存不下也照样在这一页等 */
  }
}

export function forgetAway(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    sessionStorage.removeItem(awayKey(userId));
  } catch {
    /* 存不了就没有要清的 */
  }
}

/**
 * “领养了在外面的居民也能先入住”开没开（能力表 identity.move_in_while_away，键与迎接确认框共用 adoptFlow 的 MOVE_IN_WHILE_AWAY）：
 * 读全站共用的 /meta 缓存，不另外请求。还没读到是 unknown（先不认旧的等待，也不清）；读不到、没有这一项、演示模式按没开（旧的处理）。
 */
export function useMoveInWhileAway(): "on" | "off" | "unknown" {
  const services = useServices();
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: () => services.platform.meta(), staleTime: 60_000, enabled: env.dataMode === "live" });
  if (env.dataMode !== "live" || meta.isError) return "off";
  if (!meta.isSuccess) return "unknown";
  return (meta.data.capabilities ?? []).some((capability) => capability.key === MOVE_IN_WHILE_AWAY && capability.status === "available") ? "on" : "off";
}

export interface AwayView {
  /** backAtPhrase 给的“大约 19:23 回到驿站”；说不准就是 null */
  phrase: string | null;
  /** 到点了：“接 TA 回家”变成主按钮，句子换成“应该已经回到驿站了” */
  due: boolean;
}

/**
 * 此刻（nowMs 由调用方传）等待页怎么说、到没到点。
 * - 刚知道时（seenAt）就说不准（null、认不出、本机钟已经过了那个时间）：一直用不带时间的那句，不会自己到点；
 * - 那个时间还没到：按此刻说“大约 … 回到驿站”；
 * - 那个时间已经过了：宽限内句子停在知道时的说法，过了宽限就到点。
 */
export function awayView(wait: AwayWait, nowMs: number): AwayView {
  const seen = new Date(wait.seenAt);
  const knowable = backAtPhrase(wait.backAt, seen) !== null;
  const passed = knowable && backAtPhrase(wait.backAt, new Date(nowMs)) === null;
  return {
    phrase: backAtPhrase(wait.backAt, passed ? seen : new Date(nowMs)),
    due: passed && nowMs >= wait.seenAt + AWAY_CLOCK_GRACE_MS,
  };
}

/** 等待页此刻的说法：有说得准的时间、又还没到点时，每半分钟对一次钟，切回前台也对一次；到点或说不准就不再对。 */
export function useAwayView(wait: AwayWait | null): AwayView | null {
  const [nowMs, setNowMs] = useState(() => serverNow());
  const view = wait ? awayView(wait, nowMs) : null;
  const ticking = Boolean(view && view.phrase !== null && !view.due);
  useEffect(() => {
    if (!ticking) return;
    const tick = () => setNowMs(serverNow());
    tick();
    const timer = window.setInterval(tick, AWAY_TICK_MS);
    const onVisibility = () => {
      if (document.visibilityState !== "hidden") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [ticking, wait]);
  return view;
}

/** TA 此刻在做什么：公开居民数据里有、而且说 TA 在外面或在路上时才说一句；其余（拿不到、在驿站、不知道在哪）一律不说。 */
function AwayNow({ petId }: { petId: string }) {
  const { pets } = useServices();
  const view = useQuery({ queryKey: queryKeys.publicPet(petId), queryFn: () => pets.publicPet(petId), refetchInterval: 60_000, retry: false });
  const resident = view.data?.resident ?? null;
  if (!resident) return null;
  const group = presenceGroup(resident.presence);
  if (group !== "out" && group !== "road") return null;
  const doing = nowLine(resident).trim();
  return doing ? <p className="ps-movein-away__now">此刻：{doing}</p> : null;
}

/**
 * 等待的那一块（在页面顶上的卡片里）：一句话（开头粗体）、此刻在做什么（有才说）、两个去处。
 * 每次知道“还在外面”（新的 409）就把这一块滚进视野（不做滚动动画）：点的是底下的按钮，说明在上面。
 */
export function MoveInAway({ name, wait, phrase, due }: { name: string; wait: AwayWait; phrase: string | null; due: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  // 每个 409 都换一份新的等待（同一毫秒里连回两次也算两次），所以按这一份本身、不按 seenAt 的时刻
  useEffect(() => {
    box.current?.scrollIntoView?.({ block: "nearest", behavior: "instant" });
  }, [wait]);
  return (
    <div className="ps-movein-away" ref={box}>
      {due ? (
        <p className="ps-movein-away__state" role="status">
          <strong>{name} 应该已经回到驿站了</strong>，点一下接 TA 回家。
        </p>
      ) : phrase ? (
        <p className="ps-movein-away__state" role="status">
          {/* “大约 20:34 回到驿站”整段不断行（320 宽下曾拆成“驿 / 站”）；句号跟在后面，不会单独起行 */}
          <strong>{name} 还在外面</strong>，<span className="ps-movein-away__when" style={{ whiteSpace: "nowrap" }}>{phrase}</span>。到时候点一下就能接 TA 回家。
        </p>
      ) : wait.backAt === undefined ? (
        <p className="ps-movein-away__state" role="status">
          <strong>{name} 还在外面</strong>，TA 不会瞬移回家。等 TA 回到驿站，再来接 TA 回家。
        </p>
      ) : (
        <p className="ps-movein-away__state" role="status">
          <strong>{name} 应该快回到驿站了</strong>，过一会儿再来接 TA。
        </p>
      )}
      <AwayNow petId={wait.petId} />
      <nav className="ps-movein-away__links" aria-label={`等 ${name} 回来的时候`}>
        <Link to={`/world/residents/${encodeURIComponent(wait.petId)}`}>看看 TA 的主页</Link>
        <Link to="/world">去星球上逛逛</Link>
      </nav>
    </div>
  );
}
